import { useState, useRef, useEffect, useCallback } from "react"

/**
 * Custom hook for managing horizontal scroll controls in day view
 * @param {string} viewMode - Current view mode
 * @param {any} calendarData - Calendar data (used to trigger scroll check on data change)
 * @returns {Object} - Scroll state and handlers
 */
export const useScrollControls = (viewMode, calendarData) => {
    const dayGridRef = useRef(null)
    const [showLeftArrow, setShowLeftArrow] = useState(false)
    const [showRightArrow, setShowRightArrow] = useState(true)

    // Check scroll position and update arrow visibility
    const checkScrollPosition = useCallback(() => {
        if (dayGridRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = dayGridRef.current
            setShowLeftArrow(scrollLeft > 0)
            setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10)
        }
    }, [])

    // Sync scroll position between header and body rows
    const syncScrollPosition = useCallback((scrollLeft) => {
        // Get all asset column containers in the body
        const assetColumnScrolls = document.querySelectorAll('.assetColumnsScroll')
        assetColumnScrolls.forEach(scroll => {
            scroll.scrollLeft = scrollLeft
        })
    }, [])

    // Handle horizontal scroll for day view assets
    const handleScroll = useCallback((direction) => {
        if (dayGridRef.current) {
            const scrollAmount = 300 // Scroll by 300px
            const currentScroll = dayGridRef.current.scrollLeft
            const newScroll = direction === "left"
                ? currentScroll - scrollAmount
                : currentScroll + scrollAmount

            dayGridRef.current.scrollTo({
                left: newScroll,
                behavior: "smooth"
            })

            // Sync scroll position to all asset column rows
            syncScrollPosition(newScroll)

            // Update arrow visibility after scroll
            setTimeout(() => checkScrollPosition(), 300)
        }
    }, [syncScrollPosition, checkScrollPosition])

    // Handle header scroll and sync with body
    const handleHeaderScroll = useCallback((e) => {
        const scrollLeft = e.target.scrollLeft
        syncScrollPosition(scrollLeft)
        checkScrollPosition()
    }, [syncScrollPosition, checkScrollPosition])

    // Check scroll position on mount and when view mode changes
    useEffect(() => {
        if (viewMode === "day") {
            // Use setTimeout to ensure DOM is fully rendered
            setTimeout(() => checkScrollPosition(), 100)
        }
    }, [viewMode, calendarData, checkScrollPosition])

    return {
        dayGridRef,
        showLeftArrow,
        showRightArrow,
        handleScroll,
        handleHeaderScroll
    }
}
